<script lang="ts">
  import InspectorField from '../InspectorField.svelte';
  import ConfirmDialog from '../../ui/ConfirmDialog.svelte';
  import { patchRoom, deleteRoom, getErrorMessage } from '$lib/api';
  import { clearEntity } from '$lib/router';
  import { roomsQuery } from '$lib/queries/rooms';
  import { zonesQuery } from '$lib/queries/zones';
  import { loadsQuery } from '$lib/queries/loads';
  import { invalidateAfterRoomCreate, invalidateAfterRoomDelete } from '$lib/state/invalidation';
  import { router } from '$lib/router';

  let { id }: { id: number } = $props();

  const rooms = roomsQuery();
  const zones = zonesQuery();
  const loads = loadsQuery();

  let room = $derived((rooms.data ?? []).find((r) => r.id === id));
  let loadCount = $derived(room ? (loads.data ?? []).filter((l) => l.room === room.id).length : 0);

  let showDeleteConfirm = $state(false);
  let selectError = $state('');

  async function saveName(name: string) {
    await patchRoom(id, { name });
    invalidateAfterRoomCreate(router.unitId!);
  }

  async function saveDescription(description: string) {
    await patchRoom(id, { description });
    invalidateAfterRoomCreate(router.unitId!);
  }

  async function saveZone(event: Event) {
    const zoneId = Number((event.currentTarget as HTMLSelectElement).value);
    if (!zoneId || zoneId === room?.zone) return;
    selectError = '';
    try {
      await patchRoom(id, { zone: zoneId });
      invalidateAfterRoomCreate(router.unitId!);
    } catch (e) {
      selectError = getErrorMessage(e) || 'Failed to move room';
    }
  }

  async function handleDelete() {
    showDeleteConfirm = false;
    if (!room) return;
    await deleteRoom(room.id);
    invalidateAfterRoomDelete(router.unitId!);
    clearEntity();
  }
</script>

{#if room}
  <div class="px-3 py-3 space-y-4">
    <div class="space-y-2">
      <InspectorField label="Name" value={room.name} onSave={saveName} />

      <div class="space-y-1">
        <label class="block text-2xs font-medium text-text-muted uppercase tracking-wide">
          Zone
        </label>
        <select
          class="w-full px-2 py-1.5 text-sm border border-border-strong rounded bg-bg-canvas text-text-primary focus:outline-none focus:ring-1 focus:ring-accent-primary"
          value={room.zone}
          onchange={saveZone}
        >
          {#each zones.data ?? [] as zone}
            <option value={zone.id}>{zone.name}</option>
          {/each}
        </select>
        {#if selectError}
          <p class="text-2xs text-state-error">{selectError}</p>
        {/if}
      </div>

      <InspectorField
        label="Notes"
        type="textarea"
        value={(room as { description?: string }).description || ''}
        placeholder="Room notes"
        onSave={saveDescription}
      />

      <div class="space-y-1">
        <p class="text-2xs font-medium text-text-muted uppercase tracking-wide">Load count</p>
        <p class="text-sm font-mono text-text-primary">{loadCount}</p>
      </div>
    </div>

    <div class="pt-4 border-t border-border-default space-y-2">
      <p class="text-2xs font-medium text-text-muted uppercase tracking-wide">Danger zone</p>
      <button
        type="button"
        class="w-full px-2 py-1.5 text-sm text-state-error border border-state-error/30 rounded hover:bg-state-error/5 transition-colors duration-100"
        onclick={() => (showDeleteConfirm = true)}
      >
        Delete room
      </button>
    </div>
  </div>
{:else}
  <div class="px-3 py-3 text-sm text-text-muted">Room not found</div>
{/if}

<ConfirmDialog
  bind:open={showDeleteConfirm}
  title="Delete room"
  message="Delete “{room?.name}”? All loads in this room will also be removed."
  confirmText="Delete"
  onconfirm={handleDelete}
  oncancel={() => (showDeleteConfirm = false)}
/>
